import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Grid,
  IconButton,
  Divider,
  Chip,
  Paper,
  Avatar,
  Stack
} from '@mui/material';
import {
  Info as InfoIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  AccountTree as AccountTreeIcon,
  CheckCircle as CheckCircleIcon,
  EventAvailable as EventAvailableIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { useTranslations } from '../../constants/translations/CaseTranslation';
import { formatDate, formatDateTime } from '../../constants/DateUtils';

const CaseInfoModal = ({ 
  open, 
  onClose, 
  caseData, 
  officeColor 
}) => {
  const t = useTranslations(caseData?.language);

  if (!caseData) return null;

  const getMemberByRole = (role) => {
    if (!caseData.case_members || caseData.case_members.length === 0) {
      return null;
    }
    return caseData.case_members.find(member => member.role === role);
  };

  const getMemberName = (member) => {
    if (!member) return t.notAssigned;
    
    if (member?.full_name) {
      return member.full_name.replace(';', '');
    }
    
    if (member.name_str) {
      return member.name_str;
    }
    
    return t.notAssigned;
  };

  // Función para obtener el color del estado
  const getStatusColor = (state) => {
    switch (state) {
      case 'draft': return '#9e9e9e';
      case 'initiated': return officeColor || '#1976d2';
      case 'processing': return '#ff9800';
      case 'completed': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  // Función para obtener el label del estado
  const getStatusLabel = (state) => {
    switch (state) {
      case 'draft': return t.statusDraft;
      case 'initiated': return t.statusInitiated;
      case 'processing': return t.statusProcessing;
      case 'completed': return t.statusCompleted;
      default: return state;
    }
  };

  // Función para obtener el nombre del cliente
  const getClientName = () => {
    if (caseData.client?.full_name) {
      return caseData.client.full_name.replace(';', '');
    }
    if (caseData.client_str) {
      return caseData.client_str;
    }
    return t.notAssigned;
  };

  // Obtener iniciales para avatares
  const getInitials = (name) => {
    if (!name || name === t.notAssigned) return '?';
    const parts = name.replace(';', '').split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  const getMembersByRole = (role) => {
    if (!caseData.case_members || caseData.case_members.length === 0) {
      return [];
    }
    return caseData.case_members.filter(member => member.role === role);
  };

  const clientMember = getMemberByRole('client');
  const lawyerMember = getMemberByRole('lawyer');
  const partnerMembers = getMembersByRole('partner');
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        backgroundColor: `${officeColor}10`,
        borderBottom: `3px solid ${officeColor}`,
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ backgroundColor: officeColor, width: 48, height: 48 }}>
            <InfoIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="700">
              {t.caseInformation || 'Información del Expediente'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.caseNumber}: {caseData.number}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={onClose}
          sx={{ 
            color: officeColor,
            '&:hover': { backgroundColor: `${officeColor}20` }
          }}
          aria-label={t.closeModal}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3, px: 4 }}>
        <Grid container spacing={3}>
          {/* Columna Izquierda - Información Principal */}
          <Grid item size={{ xs: 12, md: 8 }}>
            {/* Título y Estado */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="overline" color="text.secondary" fontWeight="600">
                    {t.title || 'Título'}
                  </Typography>
                  <Typography variant="h6" fontWeight="600" sx={{ mt: 0.5, mb: 1 }}>
                    {caseData.title}
                  </Typography>
                </Box>
                <Chip 
                  label={getStatusLabel(caseData.state)} 
                  icon={caseData.state === 'completed' ? <CheckCircleIcon /> : undefined}
                  sx={{ 
                    backgroundColor: getStatusColor(caseData.state),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    height: 32,
                    '& .MuiChip-icon': { color: 'white' }
                  }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Descripción */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <DescriptionIcon sx={{ color: officeColor, fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight="600" color={officeColor}>
                    {t.description || 'Descripción'}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ lineHeight: 1.7, color: 'text.secondary', pl: 4 }}>
                  {caseData.description || t.noDescriptionAvailable}
                </Typography>
              </Box>
            </Paper>

            {/* Clasificación del Expediente */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CategoryIcon sx={{ color: officeColor }} />
                <Typography variant="h6" fontWeight="600">
                  {t.classification || 'Clasificación'}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Área Práctica */}
                {caseData.office_practice_area?.practice_area?.name && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <AccountTreeIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.practiceArea || 'Área Práctica'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {caseData.office_practice_area.practice_area.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Forma */}
                {caseData.office_status?.form?.name && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <LabelIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.form || 'Forma'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {caseData.office_status.form.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Tipo de Expediente */}
                {caseData.office_status?.case_type?.name && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <AssignmentIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.caseType || 'Tipo de Expediente'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {caseData.office_status.case_type.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Estatus */}
                {caseData.office_status?.name && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <CheckCircleIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.status || 'Estatus'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {caseData.office_status.name}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Fechas del Expediente */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <EventIcon sx={{ color: officeColor }} />
                <Typography variant="h6" fontWeight="600">
                  {t.dates || 'Fechas'}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Fecha de Inicio */}
                {caseData.start_date && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <EventAvailableIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.startDate || 'Fecha de Inicio'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {formatDate(caseData.start_date, caseData.language)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Fecha de Fin */}
                {caseData.end_date && (
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <EventIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                          {t.endDate || 'Fecha de Finalización'}
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {formatDate(caseData.end_date, caseData.language)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Fecha de Creación */}
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CalendarIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="600">
                        {t.creationDate || 'Fecha de Creación'}
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDateTime(caseData.created_at, caseData.language)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Última Modificación */}
                <Grid item size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <ScheduleIcon sx={{ color: officeColor, fontSize: 20, mt: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="600">
                        {t.lastModification || 'Última Modificación'}
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {formatDateTime(caseData.updated_at, caseData.language)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Columna Derecha - Partes Involucradas */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, bgcolor: `${officeColor}08`, borderRadius: 2, border: `1px solid ${officeColor}30` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonIcon sx={{ color: officeColor }} />
                <Typography variant="h6" fontWeight="600">
                  {t.parties || 'Partes Involucradas'}
                </Typography>
              </Box>

              <Stack spacing={3}>
                {/* Cliente */}
                {clientMember && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ mb: 1.5, display: 'block' }}>
                      {t.client || 'CLIENTE'}
                    </Typography>
                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 48, 
                            height: 48, 
                            backgroundColor: officeColor,
                            fontSize: '1rem',
                            fontWeight: 600,
                            // Indicador visual si es texto vs usuario
                            opacity: clientMember.person_id ? 1 : 0.7,
                            border: clientMember.person_id ? 'none' : '2px dashed',
                            borderColor: officeColor
                          }}
                        >
                          {getInitials(getMemberName(clientMember))}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body1" fontWeight="600">
                              {getMemberName(clientMember)}
                            </Typography>
                            {/* Indicador de tipo de miembro */}
                            {!clientMember.person_id && (
                              <Chip 
                                label={t.textLabel || 'Texto'} 
                                size="small"
                                sx={{ 
                                  height: 18,
                                  fontSize: '0.65rem',
                                  backgroundColor: officeColor,
                                  color: 'white'
                                }}
                              />
                            )}
                          </Box>
                          
                          {/* Email (solo si es usuario del sistema) */}
                          {clientMember?.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {clientMember.email}
                              </Typography>
                            </Box>
                          )}

                          {/* Documento */}
                          {clientMember?.document_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <AssignmentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {clientMember.document_number}
                              </Typography>
                            </Box>
                          )}

                          {/* Teléfono */}
                          {clientMember?.phone_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {clientMember.phone_number}
                              </Typography>
                            </Box>
                          )}

                          {/* Mensaje informativo para miembros de texto */}
                          {!clientMember.person_id && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: officeColor, borderRadius: 1, border: '1px solid', borderColor: 'black' }}>
                              <Typography variant="caption" color="white" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                {t.canBeLinkedLater || 'Puede vincularse a un usuario posteriormente'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Abogado Principal */}
                {lawyerMember && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="600" sx={{ mb: 1.5, display: 'block' }}>
                      {t.assignedLawyer || 'ABOGADO PRINCIPAL'}
                    </Typography>
                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 48, 
                            height: 48, 
                            backgroundColor: 'grey.700',
                            fontSize: '1rem',
                            fontWeight: 600,
                            opacity: lawyerMember.person_id ? 1 : 0.7,
                            border: lawyerMember.person_id ? 'none' : '2px dashed',
                            borderColor: 'grey.700'
                          }}
                        >
                          {getInitials(getMemberName(lawyerMember))}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body1" fontWeight="600">
                              {getMemberName(lawyerMember)}
                            </Typography>
                            {!lawyerMember.person_id && (
                              <Chip 
                                label={t.textLabel || 'Texto'} 
                                size="small"
                                sx={{ 
                                  height: 18,
                                  fontSize: '0.65rem',
                                  backgroundColor: officeColor,
                                  color: 'white'
                                }}
                              />
                            )}
                          </Box>
                          
                          {/* Email */}
                          {lawyerMember?.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {lawyerMember.email}
                              </Typography>
                            </Box>
                          )}

                          {/* Documento */}
                          {lawyerMember?.document_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <AssignmentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {lawyerMember.document_number}
                              </Typography>
                            </Box>
                          )}

                          {/* Teléfono */}
                          {lawyerMember?.phone_number && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {lawyerMember.phone_number}
                              </Typography>
                            </Box>
                          )}

                          {!lawyerMember.person_id && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                              <Typography variant="caption" color="warning.dark" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                {t.canBeLinkedLater || 'Puede vincularse a un usuario posteriormente'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  </Box>
                )}

                {/* Colaboradores (Múltiples) */}
                {partnerMembers && partnerMembers.length > 0 && (
                  <Box>
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      fontWeight="600" 
                      sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      {partnerMembers.length === 1 
                        ? (t.assignedPartner || 'COLABORADOR')
                        : (t.assignedPartners || 'COLABORADORES')
                      }
                      {partnerMembers.length > 1 && (
                        <Chip 
                          label={partnerMembers.length} 
                          size="small"
                          sx={{ 
                            height: 18,
                            fontSize: '0.65rem',
                            backgroundColor: officeColor,
                            color: 'white'
                          }}
                        />
                      )}
                    </Typography>
                    
                    <Stack spacing={2}>
                      {partnerMembers.map((partnerMember, index) => (
                        <Paper key={index} elevation={1} sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Avatar 
                              sx={{ 
                                width: 48, 
                                height: 48, 
                                backgroundColor: 'grey.500',
                                fontSize: '1rem',
                                fontWeight: 600,
                                opacity: partnerMember.person_id ? 1 : 0.7,
                                border: partnerMember.person_id ? 'none' : '2px dashed',
                                borderColor: 'grey.500'
                              }}
                            >
                              {getInitials(getMemberName(partnerMember))}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight="600">
                                  {getMemberName(partnerMember)}
                                </Typography>
                                {!partnerMember.person_id && (
                                  <Chip 
                                    label={t.textLabel || 'Texto'} 
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      backgroundColor: officeColor,
                                      color: 'white'
                                    }}
                                  />
                                )}
                                {/* Badge de posición si hay múltiples */}
                                {partnerMembers.length > 1 && (
                                  <Chip 
                                    label={`#${index + 1}`} 
                                    size="small"
                                    sx={{ 
                                      height: 18,
                                      fontSize: '0.65rem',
                                      backgroundColor: 'grey.200',
                                      color: 'grey.700'
                                    }}
                                  />
                                )}
                              </Box>
                              
                              {/* Email */}
                              {partnerMember.person?.email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                  >
                                    {partnerMember.person.email}
                                  </Typography>
                                </Box>
                              )}

                              {/* Documento */}
                              {partnerMember.person?.document_number && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <AssignmentIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {partnerMember.person.document_number}
                                  </Typography>
                                </Box>
                              )}

                              {/* Teléfono */}
                              {partnerMember.person?.phone_number && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {partnerMember.person.phone_number}
                                  </Typography>
                                </Box>
                              )}

                              {!partnerMember.person_id && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'black' }}>
                                  <Typography variant="caption" color={officeColor} sx={{ display: 'block', fontSize: '0.7rem' }}>
                                    {t.canBeLinkedLater || 'Puede vincularse a un usuario posteriormente'}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Mensaje si no hay miembros */}
                {!clientMember && !lawyerMember && (!partnerMembers || partnerMembers.length === 0) && (
                  <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t.noMembersAssigned || 'No hay miembros asignados a este expediente'}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 4, py: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{ 
            backgroundColor: officeColor,
            px: 4,
            '&:hover': { 
              backgroundColor: officeColor, 
              opacity: 0.9 
            }
          }}
        >
          {t.close || 'Cerrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaseInfoModal;